import {useDispatch, useSelector} from 'react-redux';
import {useEffect, useState} from 'react';
import cn from 'classnames';
import {type SystemNotification, type SystemNotificationAction} from '@qep/common';
import {getQepSystem} from '@qep/shared/src';
import {applicationReducer, selectSystemNotifications} from '../../../state/reducer';
import {SystemNotificationModal} from '../SystemNotificationModal/system-notification-modal';
import './system-notification-banner.scss';

type SystemNotificationWithAcknowledged = SystemNotification & {acknowledged: boolean};

/**
 * Displays active system notifications and releases its live subscription when
 * the browser shell unmounts to prevent duplicate callbacks after remounts.
 */
export function SystemNotificationBanner() {
  const [open, setOpen] = useState(false);
  const [activeNotification, setActiveNotification] = useState<string | undefined>(undefined);
  const dispatch = useDispatch();
  const notifications = useSelector(selectSystemNotifications);

  useEffect(() => {
    /** Dispatches each validated subscription action into the browser application store. */
    const handleSystemNotification = (action: SystemNotificationAction): void => {
      dispatch(action);
    };

    const unsubscribe = getQepSystem().apiServices.notifications.subscribeToSystemNotifications(handleSystemNotification);

    /** Releases the live callback when React disposes the effect, preventing stale subscribers. */
    const cleanupSubscription = (): void => {
      unsubscribe();
    };

    return cleanupSubscription;
  }, [dispatch]);

  useEffect(() => {
    const critical = notifications.find(n => n.level === 'critical' && !n.acknowledged);
    if (!open && !activeNotification && critical) {
      setOpen(true);
      setActiveNotification(String(critical.id));
    }
  }, [notifications, open, activeNotification]);

  if (notifications.length === 0) {
    return null;
  }

  return (
    <>
      <div className='browser-system-notification-list'>
        {notifications.map(notification => {
          const notificationId = String(notification.id);

          return (
            <a
              key={notificationId}
              className={cn('browser-system-notification-item', {
                'browser-system-notification-critical': notification.level === 'critical',
              })}
              onClick={() => {
                setOpen(true);
                setActiveNotification(notificationId);
              }}
            >
              {notification.title}
            </a>
          );
        })}
      </div>
      <SystemNotificationModal
        open={open}
        notificationId={activeNotification}
        handleClose={(notificationId?: string) => {
          setOpen(false);
          if (notificationId) {
            dispatch(applicationReducer.actions.acknowledgeSystemNotification(notificationId));
          }
        }}
      />
    </>
  );
}
